import React from "react";
import FormInput from "../../Ui/FormInput";
import FormSelect from "../../FormFields/FormSelect";
import SelectCity from "../../Admin/SelectCity";
import { lettreVoieList, typeVoie } from "../../../Utils/Function";
import { AddressData, AddressErrors } from "../../../Hooks/InterfaceAiciOnboarding";
import { COMPLEMENT_MAX, STREET_LABEL_MAX } from "../../../Utils/manualAddress";

interface ManualAddressFieldsProps {
    value: AddressData;
    errors: AddressErrors;
    onChange: (patch: Partial<AddressData>) => void;
    /** Lieu-dit : utile à l'URSSAF (inscription AICI), pas conservé sur l'adresse du profil. */
    showLieuDit?: boolean;
}

/**
 * Champs d'adresse au format du formulaire AICI (numéro obligatoire). Partagé
 * entre l'étape Adresse de l'inscription AICI et le profil du client.
 */
const ManualAddressFields = ({ value, errors, onChange, showLieuDit = false }: ManualAddressFieldsProps) => {
    const letterIndex = value.streetLetter
        ? lettreVoieList.findIndex((l) => l.name === value.streetLetter!.name)
        : -1;
    const typeIndex = value.streetType
        ? typeVoie.findIndex((t) => t.name === value.streetType!.name)
        : -1;

    return (
        <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormInput
                    label="N°"
                    placeholder="Ex : 5"
                    value={value.streetNumber}
                    onChange={(e) => onChange({ streetNumber: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                    error={errors.streetNumber}
                    required
                    isEditing
                />
                <FormSelect
                    label="Lettre"
                    placeholder="Ex : A"
                    data={lettreVoieList.map((l) => l.name)}
                    defaultValue={letterIndex >= 0 ? letterIndex + 1 : ""}
                    callback={(idx) => onChange({ streetLetter: lettreVoieList[idx - 1] ?? null })}
                />
            </div>

            <FormSelect
                label="Type de voie"
                placeholder="Ex : Rue"
                data={typeVoie.map((t) => t.name)}
                defaultValue={typeIndex >= 0 ? typeIndex + 1 : ""}
                callback={(idx) => onChange({ streetType: typeVoie[idx - 1] ?? null })}
                error={errors.streetType}
                required
            />

            <FormInput
                label="Libellé de voie"
                placeholder="Ex : Roger Martin Du Gard"
                value={value.streetLabel}
                onChange={(e) => onChange({ streetLabel: e.target.value.slice(0, STREET_LABEL_MAX) })}
                error={errors.streetLabel}
                required
                isEditing
            />

            <FormInput
                label="Complément d'adresse"
                placeholder="Ex : Appartement 12, Bâtiment B"
                value={value.complement}
                onChange={(e) => onChange({ complement: e.target.value.slice(0, COMPLEMENT_MAX) })}
                error={errors.complement}
                isEditing
            />

            {showLieuDit && (
                <FormInput
                    label="Lieu-dit"
                    placeholder="Ex : Gamarde"
                    value={value.lieuDit}
                    onChange={(e) => onChange({ lieuDit: e.target.value.slice(0, COMPLEMENT_MAX) })}
                    error={errors.lieuDit}
                    isEditing
                />
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormInput
                    label="Code postal"
                    placeholder="Ex : 92390"
                    value={value.zipCode}
                    onChange={(e) => onChange({ zipCode: e.target.value.replace(/\D/g, "").slice(0, 5) })}
                    error={errors.zipCode}
                    required
                    isEditing
                />
                <SelectCity
                    label="Ville"
                    zipCode={value.zipCode}
                    value={value.city}
                    callBack={(v) => onChange({ city: v })}
                    error={errors.city}
                    required
                />
            </div>
        </div>
    );
};

export default ManualAddressFields;
